/**
 * THE COMPOUNDING LIFE — header behaviour (DEV).
 * - Scroll state (transparent -> solid)
 * - Keeps --header-height in sync for Horizon sections
 * - Mobile menu (native <dialog>)
 * Cart is handled entirely by Horizon's native cart components.
 */
class TclHeader extends HTMLElement {
  #ticking = false;
  #menu = null;
  #toggle = null;
  #resizeObserver = null;
  #mq = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    this.#menu = this.querySelector('[data-tcl-menu]');
    this.#toggle = this.querySelector('[data-tcl-menu-open]');

    this.#updateScrolled();
    window.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#resizeObserver = new ResizeObserver(this.#syncHeight);
    this.#resizeObserver.observe(this);
    this.#syncHeight();

    this.#toggle?.addEventListener('click', this.#openMenu);
    this.querySelector('[data-tcl-menu-close]')?.addEventListener('click', this.#closeMenu);
    this.#menu?.addEventListener('close', this.#onMenuClosed);
    this.#menu?.addEventListener('click', this.#onMenuClick);
    this.#mq.addEventListener('change', this.#onBreakpoint);
    document.addEventListener('cart:update', this.#onCartUpdate);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    this.#resizeObserver?.disconnect();
    this.#mq.removeEventListener('change', this.#onBreakpoint);
    document.removeEventListener('cart:update', this.#onCartUpdate);
    document.documentElement.style.removeProperty('overflow');
  }

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#updateScrolled();
      this.#ticking = false;
    });
  };

  #updateScrolled() {
    // Over a cinematic hero (Hero V2) the bar stays transparent until the hero
    // has been scrolled past; everywhere else it turns solid after 24px.
    let threshold = 24;
    const cine = this.dataset.mode === 'overlay' && document.querySelector('[data-tcl-hero-cine].is-cine');
    if (cine) {
      const r = cine.getBoundingClientRect();
      threshold = window.scrollY + r.bottom - this.offsetHeight;
    }
    const scrolled = window.scrollY > threshold;
    const next = scrolled ? 'true' : 'false';
    if (this.dataset.scrolled !== next) this.dataset.scrolled = next;
  }

  #syncHeight = () => {
    const h = this.offsetHeight;
    document.body.style.setProperty('--header-height', `${h}px`);
    document.body.style.setProperty('--header-group-height', '0px');
  };

  #openMenu = () => {
    if (!this.#menu || this.#menu.open) return;
    this.#menu.showModal();
    this.#toggle?.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
  };

  #closeMenu = () => {
    this.#menu?.close();
  };

  #onMenuClosed = () => {
    this.#toggle?.setAttribute('aria-expanded', 'false');
    document.documentElement.style.removeProperty('overflow');
    this.#toggle?.focus({ preventScroll: true });
  };

  #onMenuClick = (event) => {
    const link = event.target.closest('[data-tcl-menu-link]');
    if (!link) return;

    if (link.hasAttribute('data-tcl-open-cart')) {
      const drawer = this.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        this.#closeMenu();
        requestAnimationFrame(() => drawer.open());
        return;
      }
    }
    // In-page anchors: close so the page can scroll.
    this.#closeMenu();
  };

  #onBreakpoint = (e) => {
    if (e.matches && this.#menu?.open) this.#closeMenu();
  };

  #onCartUpdate = () => {
    // Mirror the native cart count into the mobile menu label.
    requestAnimationFrame(() => {
      const native = this.querySelector('.cart-bubble__text-count');
      const target = this.querySelector('[data-tcl-cart-count]');
      if (!native || !target) return;
      const n = parseInt(native.textContent || '0', 10);
      target.textContent = n > 0 ? ` (${n})` : '';
    });
  };
}

if (!customElements.get('tcl-header')) {
  customElements.define('tcl-header', TclHeader);
}
